import _ from 'lodash';
import React from 'react';
import cn from 'classnames';
import { connect } from 'react-redux';
import { createPortal } from 'react-dom';

import { isLayerType } from 'common/utils/is-layer-type';
import { COMPONENT_TYPE } from 'common/constants';
import { setEditorSelectedNode } from 'client/actions';
import { SelectionHintController } from 'client/components/common/SelectionHint/SelectionHintController';
import { SelectionHintEvent } from 'client/components/common/SelectionHint/SelectionHintEvent';
import { Context as ResponsiveProviderContext } from 'client/components/common/ResponsiveProvider/Context';
import { BOX_TYPE } from 'client/components/common/BuildingBlocks/Box';
import {
	SELECTION_STAGE,
	SELECTION_TYPES,
	getSelectionPosition,
	getSelectionGroupPosition,
} from 'client/components/common/SelectionHint/utils';
import Points from './Draggable/Points';
import { SUPPORTED_PROP } from './Draggable/Points/constants';
import ContentResize from './Draggable/ContentResize';
import Breadcrumbs from './Breadcrumbs';
import css from './SectionHint.scss';
import { SelectionType } from './types';

const isChildOf = (selection, parentType) =>
	parentType ===
	_.last(
		_.get(
			_.last(_.get(selection, 'editableModeProps.inheritance.parent', [])),
			'inheritance.componentProps.other.boxType',
			[{}]
		)
	).value;

const isStack = props => _.get(props, 'uiConfig.componentProps.other.boxType') === BOX_TYPE.STACK;
const isGrid = props => _.get(props, 'uiConfig.componentProps.other.boxType') === BOX_TYPE.GRID;
const isBBType = (props, type) => _.get(props, 'type') === type;

function isAllowToSelect({ selection, selectionType, componentProps, event }) {
	if (selectionType !== SELECTION_TYPES.clicked) {
		return true;
	}

	const clickedSelection = selection[SELECTION_TYPES.clicked];
	const isGlobal = isLayerType(componentProps).global;
	const isMultiSelect = event.shiftKey;
	const isAllowGlobal =
		_.size(clickedSelection) && isMultiSelect
			? _.values(clickedSelection).some(el => isLayerType(el).global)
			: isGlobal;

	return isAllowGlobal === isGlobal;
}

const isSelection = {
	typeOf: (selection, type) => selection.type === type,
	childOfType: (selection, type) =>
		selection.editableModeProps.inheritance.parent.some(parent => parent.type === type),
};

type SelectionHintState = any;

type SelectionHintProps = {
	currentState: string;
	selection: SelectionType;
	setEditorSelectedNode: (...args: any[]) => any;
};

class SelectionHint extends React.Component<SelectionHintProps, SelectionHintState> {
	state = {
		dragProp: '', // dragProp property name (eg. size, position...)
		dragId: '', // is of the draggable block
		isScrolling: false,
	};

	scrollTimeout = null;

	componentDidMount() {
		document.addEventListener('scroll', this.onScroll);
		window.addEventListener('resize', this.onScroll);

		// @ts-expect-error ts-migrate FIXME
		this.SelectionHintController = new SelectionHintController({
			action: this.props.setEditorSelectedNode,
		});

		// @ts-expect-error ts-migrate FIXME
		this.SelectionHintEvent = new SelectionHintEvent({
			listener: this.selectionHintEventListener,
		});
		// @ts-expect-error ts-migrate FIXME
		this.SelectionHintEvent.addListener();
	}

	shouldComponentUpdate(nextProps: any, nextState: any) {
		// shouldn't update while dragging
		if (Boolean(this.state.dragProp) && Boolean(nextState.dragProp)) return false;
		if (Boolean(this.state.dragProp) && !nextState.dragProp) return true;
		return true;
	}

	componentWillUnmount() {
		document.removeEventListener('scroll', this.onScroll);
		window.removeEventListener('resize', this.onScroll);
		// @ts-expect-error ts-migrate FIXME
		this.SelectionHintEvent.removeListener();
	}

	selectionHintEventListener = e => {
		const selectionType = _.get(e, 'detail.selectionType');
		const componentProps = _.get(e, 'detail.componentProps');
		const originalEvent = _.get(e, 'detail.originalEvent');
		const trigger = _.get(e, 'detail.trigger');
		const stage = _.get(e, 'detail.stage');

		switch (trigger) {
			case SelectionHintController.FOCUS:
				// @ts-expect-error ts-migrate FIXME
				this.SelectionHintController.focus(originalEvent, selectionType, componentProps);
				break;
			case SelectionHintController.SELECT:
				if (
					isAllowToSelect({
						selection: this.props.selection,
						componentProps,
						selectionType,
						event: originalEvent,
					})
				) {
					// @ts-expect-error ts-migrate FIXME
					this.SelectionHintController.select(originalEvent, selectionType, componentProps, stage);
				}
				break;
			case SelectionHintController.DESELECT:
				// @ts-expect-error ts-migrate FIXME
				this.SelectionHintController.deselect(originalEvent, selectionType);
				break;
			default:
				break;
		}
	};

	onScroll = event => {
		if (!this.scrollTimeout) {
			this.setState({ isScrolling: true });
		}
		// @ts-expect-error ts-migrate FIXME
		clearTimeout(this.scrollTimeout);
		// @ts-expect-error ts-migrate FIXME
		this.scrollTimeout = setTimeout(this.onScrollEnd, 100);
	};

	onScrollEnd = () => {
		this.scrollTimeout = null;
		this.setState({ isScrolling: false });
	};

	/**
	 * @param dragProp {String}
	 * @param dragId {String}
	 */
	setDragging = (dragProp, dragId) => {
		this.setState({ dragProp, dragId });
	};

	/**
	 * Render draggable points to update selected element style on drag
	 * @param type {String}
	 * @param selection {Object} selection data object or object with a pair key value for each selection
	 * @param selectionStyle {Object}
	 * @param currentMediaQuery {String}
	 * @param selectionGroup {Object|null}
	 * @param isMultiple {Boolean}
	 * @param boundaries {Object} media query boundary values
	 * @return {null|*}
	 */
	renderPoints = ({ type, selection, selectionStyle, currentMediaQuery, selectionGroup, isMultiple, boundaries }) => {
		if (
			type === SELECTION_TYPES.hovered ||
			isSelection.typeOf(selection, COMPONENT_TYPE.CONTENT) ||
			isSelection.typeOf(selection, COMPONENT_TYPE.CARD)
		) {
			return null;
		}

		const isHiddenPositionPoints = Boolean(this.state.dragProp) && this.state.dragProp !== SUPPORTED_PROP.position;
		const isHiddenSizePoints = Boolean(this.state.dragProp) && this.state.dragProp !== SUPPORTED_PROP.size;

		const isStackChild = isChildOf(selection, BOX_TYPE.STACK);
		const lockX = _.get(selection, 'uiConfig.componentProps.styles.justifySelf') === 'stretch' || isStackChild;
		const lockY = _.get(selection, 'uiConfig.componentProps.styles.alignSelf') === 'stretch';
		const lockH =
			isStack(selection) ||
			isGrid(selection) ||
			isBBType(selection, COMPONENT_TYPE.TEXT) ||
			isBBType(selection, COMPONENT_TYPE.RESULT_TEXT) ||
			isBBType(selection, COMPONENT_TYPE.CHECKBOX);

		if (isChildOf(selection, BOX_TYPE.GRID)) {
			return null;
		}

		return (
			<React.Fragment key={selection.id}>
				<Points
					hidden={isHiddenPositionPoints}
					name={SUPPORTED_PROP.position}
					style={selectionStyle}
					selection={selection}
					selectionGroup={selectionGroup}
					isMultiple={isMultiple}
					setDragging={this.setDragging}
					// @ts-expect-error ts-migrate FIXME
					currentState={this.props.currentState}
					currentMediaQuery={currentMediaQuery}
					lockX={lockX}
					lockY={lockY}
					lockH={lockH}
					boundaries={boundaries}
				/>
				{!isMultiple && selection.type !== COMPONENT_TYPE.GOOGLE_LOGIN && (
					<Points
						hidden={isHiddenSizePoints}
						name={SUPPORTED_PROP.size}
						style={selectionStyle}
						selection={selection}
						selectionGroup={selectionGroup}
						isMultiple={isMultiple}
						setDragging={this.setDragging}
						// @ts-expect-error ts-migrate FIXME
						currentState={this.props.currentState}
						currentMediaQuery={currentMediaQuery}
						lockX={lockX}
						lockY={lockY}
						lockH={lockH}
						boundaries={boundaries}
					/>
				)}
			</React.Fragment>
		);
	};

	renderSelection = ({
		type,
		selectionStyle,
		selection,
		currentMediaQuery,
		position,
		selectionGroup,
		isMultiple,
		boundaries,
	}) => {
		const isRenderBreadcrumbs = !isMultiple && !this.state.dragProp;
		const isHiddenSelection = this.state.dragProp === SUPPORTED_PROP.position && this.state.dragId !== selection.id;
		const isHiddenSelectionRect = Boolean(this.state.dragProp);

		return (
			<div
				className={css.selectionW}
				data-selection-position={position}
				data-selection-type={type}
				key={`selection-${type}-${selection.id}`}
				hidden={isHiddenSelection}
			>
				<div
					className={cn(css.selection, `selection-${type}`, {
						[css.selectionAccent]:
							type === SELECTION_TYPES.clicked && selection.selectionStage === SELECTION_STAGE.INNER,
					})}
					style={selectionStyle}
					hidden={isHiddenSelectionRect}
				>
					{isRenderBreadcrumbs && <Breadcrumbs selection={selection} />}
				</div>
				{this.renderPoints({
					type,
					selection,
					selectionStyle,
					currentMediaQuery,
					isMultiple,
					selectionGroup,
					boundaries,
				})}
			</div>
		);
	};

	renderSelections = ({ currentMediaQuery, boundaries }) => {
		const clickedSelections = this.props.selection[SELECTION_TYPES.clicked];
		const isGroupSelected = _.size(clickedSelections) > 1;

		return _.map(Object.entries(this.props.selection), entity => {
			const [type, values] = entity;
			const [id, selection] = Object.entries(values)[0] || [];

			const node = document.getElementById(id);
			const isSkipRender =
				!node ||
				this.state.isScrolling ||
				// is hovered node already clicked
				(type === SELECTION_TYPES.hovered && this.props.selection[SELECTION_TYPES.clicked][id]);

			if (isSkipRender) {
				return null;
			}

			const isGroup = type === SELECTION_TYPES.clicked && isGroupSelected;
			const selectionStyle = isGroup ? getSelectionGroupPosition(clickedSelections) : getSelectionPosition(node);
			// @ts-expect-error ts-migrate FIXME
			const position = isGroup ? selectionStyle.position : window.getComputedStyle(node).position;

			return this.renderSelection({
				currentMediaQuery,
				type,
				selection,
				selectionGroup: clickedSelections,
				selectionStyle,
				position,
				isMultiple: isGroup,
				boundaries,
			});
		});
	};

	renderContentResizer = currentMediaQuery => {
		const content = document.querySelector(`[data-bb="${COMPONENT_TYPE.CONTENT}"]`);
		if (!content) {
			return null;
		}
		const component = _.first(_.values(this.props.selection[SELECTION_TYPES.clicked]));

		return createPortal(
			_.get(component, 'type') === COMPONENT_TYPE.CARD && (
				<ContentResize
					setDragging={this.setDragging}
					currentMediaQuery={currentMediaQuery}
					currentState={this.props.currentState}
					contentHeight={_.get(component, 'uiConfig.componentProps.other.contentHeight')}
				/>
			),
			// @ts-expect-error ts-migrate FIXME
			content.parentNode
		);
	};

	render() {
		return (
			<ResponsiveProviderContext.Consumer>
				{({ currentMediaQuery, boundaries }) => (
					<>
						{createPortal(this.renderSelections({ currentMediaQuery, boundaries }), document.body)}
						{this.renderContentResizer(currentMediaQuery)}
					</>
				)}
			</ResponsiveProviderContext.Consumer>
		);
	}
}

const mapStateToProps = state => ({
	selection: state.editor.selection,
	currentState: state.editor.state.state,
});
const mapDispatchToProps = {
	setEditorSelectedNode,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectionHint);
